// Pause menu over the blurred game: actions on the left, the last run and the
// room on the right. Settings open in place.

import { bindLabel } from '../../game/binds';
import { TEAM_NAMES } from '../../shared/game/mapLogic';
import { Button } from '../components/Button';
import { ColorText } from '../components/ColorText';
import { Panel } from '../components/Panel';
import { RunResultCard } from '../components/RunResultCard';
import { useApp } from '../context';
import { describeRules, peekMapMeta } from '../maps/mapMeta';
import { game, net, pauseView, runResult, settings } from '../state';
import { RoomPanel } from '../components/RoomPanel';
import { SettingsScreen } from './Settings';

export function PauseScreen() {
  const app = useApp();
  const g = game.value;
  const result = runResult.value;
  const binds = settings.value.binds;
  if (!g) return null;
  if (pauseView.value === 'settings') {
    return (
      <div class="pause-layer pause-settings layer">
        <SettingsScreen onBack={() => (pauseView.value = 'main')} />
      </div>
    );
  }
  const longname = g.info?.longname ?? app.arenas.get(g.map)?.longname ?? peekMapMeta(app.vfs, g.map)?.message;
  const rules = describeRules(g.rules);
  const spectatingMe = !!net.value.room?.members.find((m) => m.id === net.value.client?.id)?.spectating;
  const team = app.team;
  return (
    <div class="pause-layer layer" onClick={(e) => e.target === e.currentTarget && app.resume()}>
      <div class="pause-grid" onClick={(e) => e.target === e.currentTarget && app.resume()}>
        <nav class="pause-menu" aria-label="Pause menu">
          <div class="eyebrow">{g.online ? `Paused · ${net.value.room?.name ?? g.serverName}` : 'Paused · offline'}</div>
          <h1 class="pause-title">{longname ? <ColorText text={longname} /> : g.map}</h1>
          <div class="pause-sub mono">
            {g.map}
            {team !== null && ` · ${TEAM_NAMES[team]}`}
          </div>
          {rules.length > 0 && (
            <div class="chips">
              {rules.map((r) => (
                <span key={r} class="chip static">
                  {r}
                </span>
              ))}
            </div>
          )}
          <div class="menu-list">
            <Button variant="primary" size="lg" icon="play" hint="ENTER" autoFocus onClick={() => app.resume()}>
              Resume
            </Button>
            <Button variant="secondary" size="lg" icon="restart" hint={bindLabel(binds, 'respawn')} onClick={() => app.restartRun()}>
              Restart run
            </Button>
            {g.online && net.value.room && (
              <Button
                variant="secondary"
                size="lg"
                icon="eye"
                hint={bindLabel(binds, 'spectate')}
                onClick={() => (spectatingMe ? (app.stopSpectating(), app.resume()) : app.spectate('next'))}
              >
                {spectatingMe ? 'Stop spectating' : 'Spectate'}
              </Button>
            )}
            {team !== null && g.teams.length > 1 && (
              <Button variant="secondary" size="lg" icon="flag" title={`You are ${TEAM_NAMES[team]}`} onClick={() => app.changeTeam(team ? 0 : 1)}>
                Join {TEAM_NAMES[team ? 0 : 1]}
              </Button>
            )}
            <Button variant="secondary" size="lg" icon="settings" onClick={() => (pauseView.value = 'settings')}>
              Settings
            </Button>
            <Button variant="ghost" size="lg" icon="exit" onClick={() => app.leaveMap()}>
              {g.online ? 'Leave room' : 'Leave map'}
            </Button>
          </div>
          <p class="muted small">Click the game view to resume. The run timer stops while paused.</p>
        </nav>
        <div class="pause-side">
          {g.online && net.value.room && <RoomPanel />}
          {result && (
            <Panel title="Last run" class="pause-last-run">
              <RunResultCard result={result} />
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
}
